import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import article from "./StopLossContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
);

const RiskRewardCalculator = () => {
  const content = article.find((item) => item.id === 2);
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const [inputs, setInputs] = useState({
    positionType: "long",
    entryPrice: "100.00",
    targetPrice: "120.00",
    stopLossPrice: "95.00",
    quantity: "100",
    brokerage: "5.00",
    taxes: "2.00",
    slippage: "0.50",
    leverage: "1",
    currencyConversion: "1",
    trailingStop: "0",
    financingCost: "0.00",
    riskAmount: "100",
    portfolioSize: "10000",
    riskPerTrade: "2",
    probability: "60",
    timeframe: "30",
    volatility: "15",
    multipleTargets: "1",
    partialExit: "0"
  });

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);
  const [scenarioResults, setScenarioResults] = useState([]);

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateRiskReward();
    }
  }, [inputs, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const calculateRiskReward = () => {
    const {
      positionType,
      entryPrice,
      targetPrice,
      stopLossPrice,
      quantity,
      brokerage,
      taxes,
      slippage,
      leverage,
      currencyConversion,
      trailingStop,
      financingCost,
      probability,
      timeframe,
      volatility,
      multipleTargets,
      partialExit
    } = inputs;

    if (!entryPrice || !targetPrice || !stopLossPrice || !quantity) {
      return;
    }

    const entry = parseFloat(entryPrice);
    const target = parseFloat(targetPrice);
    const stop = parseFloat(stopLossPrice);
    const qty = parseFloat(quantity);
    const lev = parseFloat(leverage) || 1;
    const fee = parseFloat(brokerage || 0) + parseFloat(taxes || 0);
    const slip = parseFloat(slippage || 0);
    const trail = parseFloat(trailingStop || 0) / 100;
    const curr = parseFloat(currencyConversion || 1);
    const finance = parseFloat(financingCost || 0);
    const prob = parseFloat(probability || 50) / 100;
    const time = parseFloat(timeframe || 30);
    const vol = parseFloat(volatility || 15);
    const multiTarget = parseFloat(multipleTargets || 1);
    const partial = parseFloat(partialExit || 0) / 100;

    // Adjust for slippage
    let effectiveStop = positionType === "long" ? stop - slip : stop + slip;
    let effectiveTarget = positionType === "long" ? target - slip : target + slip;

    // Apply trailing stop
    if (trail > 0) {
      effectiveStop = positionType === "long" 
        ? Math.max(entry * (1 - trail), effectiveStop)
        : Math.min(entry * (1 + trail), effectiveStop);
    }

    // Risk and Reward calculations
    const riskPerUnit = positionType === "long" ? entry - effectiveStop : effectiveStop - entry;
    const rewardPerUnit = positionType === "long" ? effectiveTarget - entry : entry - effectiveTarget;

    // Total calculations
    const totalRisk = (riskPerUnit * qty * lev) + fee + finance;
    const totalReward = (rewardPerUnit * qty * lev) - fee;
    const netTotalRisk = totalRisk * curr;
    const netTotalReward = totalReward * curr;

    // Core ratios
    const rrRatio = rewardPerUnit / riskPerUnit;
    const riskPercentage = (riskPerUnit / entry) * 100;
    const rewardPercentage = (rewardPerUnit / entry) * 100;

    // Margin and position calculations
    const marginRequired = (entry * qty) / lev;
    const positionValue = entry * qty * lev;

    // Advanced metrics
    const expectedValue = (netTotalReward * prob) - (netTotalRisk * (1 - prob));
    const kellyCriterion = prob - ((1 - prob) / rrRatio);
    const profitFactor = (netTotalReward * prob) / (netTotalRisk * (1 - prob));
    
    // Time-adjusted metrics
    const annualizedReturn = (rewardPercentage / (time / 365)) * 100;
    const riskAdjustedReturn = annualizedReturn / (vol / 100);

    // Multiple targets calculation
    const partialProfit = netTotalReward * (partial / 100);
    const remainingPosition = qty * (1 - (partial / 100));
    const adjustedRisk = netTotalRisk * (remainingPosition / qty);

    // Scenario analysis
    const scenarios = calculateScenarios(entry, effectiveStop, effectiveTarget, qty, lev, prob);

    setResult({
      riskPerUnit: riskPerUnit,
      rewardPerUnit: rewardPerUnit,
      totalRisk: netTotalRisk,
      totalReward: netTotalReward,
      rrRatio: rrRatio,
      marginRequired: marginRequired * curr,
      riskPercentage: Math.abs(riskPercentage),
      rewardPercentage: Math.abs(rewardPercentage),
      expectedValue: expectedValue,
      kellyCriterion: kellyCriterion,
      profitFactor: profitFactor,
      annualizedReturn: annualizedReturn,
      riskAdjustedReturn: riskAdjustedReturn,
      positionValue: positionValue * curr,
      breakEvenPrice: positionType === "long" 
        ? entry + (fee / (qty * lev))
        : entry - (fee / (qty * lev)),
      partialProfit: partialProfit,
      adjustedRisk: adjustedRisk,
      winRateRequired: (1 / (1 + rrRatio)) * 100,
      scenarios: scenarios
    });

    setScenarioResults(scenarios);
  };

  const calculateScenarios = (entry, stop, target, qty, leverage, probability) => {
    const scenarios = [];
    
    // Base scenario
    scenarios.push({
      name: "Base Case",
      probability: probability * 100,
      outcome: (target - entry) * qty * leverage,
      description: "Target hit"
    });

    // Stop loss scenario
    scenarios.push({
      name: "Stop Loss",
      probability: (1 - probability) * 100,
      outcome: (stop - entry) * qty * leverage,
      description: "Stop loss triggered"
    });

    // Partial profit scenarios
    for (let i = 1; i <= 3; i++) {
      const partialTarget = entry + ((target - entry) * (i * 0.25));
      scenarios.push({
        name: `Partial ${i * 25}%`,
        probability: probability * 0.3,
        outcome: (partialTarget - entry) * qty * leverage * (i * 0.25),
        description: `${i * 25}% profit taken`
      });
    }

    return scenarios;
  };

  const calculatePositionSizing = () => {
    const { riskAmount, portfolioSize, riskPerTrade, entryPrice, stopLossPrice } = inputs;
    
    if (!riskAmount || !portfolioSize || !entryPrice || !stopLossPrice) return null;

    const riskAmt = parseFloat(riskAmount);
    const portfolio = parseFloat(portfolioSize);
    const riskPercent = parseFloat(riskPerTrade);
    const entry = parseFloat(entryPrice);
    const stop = parseFloat(stopLossPrice);

    const riskPerUnit = Math.abs(entry - stop);
    const positionSizeByRisk = Math.floor(riskAmt / riskPerUnit);
    const positionSizeByPortfolio = Math.floor((portfolio * (riskPercent / 100)) / riskPerUnit);

    return {
      positionSizeByRisk,
      positionSizeByPortfolio,
      optimalSize: Math.min(positionSizeByRisk, positionSizeByPortfolio),
      riskAmount: riskAmt,
      portfolioRisk: portfolio * (riskPercent / 100)
    };
  };

  const resetCalculator = () => {
    setInputs({
      positionType: "long",
      entryPrice: "100.00",
      targetPrice: "120.00",
      stopLossPrice: "95.00",
      quantity: "100",
      brokerage: "5.00",
      taxes: "2.00",
      slippage: "0.50",
      leverage: "1",
      currencyConversion: "1",
      trailingStop: "0",
      financingCost: "0.00",
      riskAmount: "100",
      portfolioSize: "10000",
      riskPerTrade: "2",
      probability: "60",
      timeframe: "30",
      volatility: "15",
      multipleTargets: "1",
      partialExit: "0"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Risk/Reward Ratio,${result.rrRatio.toFixed(2)}`,
      `Total Risk,${result.totalRisk.toFixed(2)}`,
      `Total Reward,${result.totalReward.toFixed(2)}`,
      `Risk Percentage,${result.riskPercentage.toFixed(2)}%`,
      `Reward Percentage,${result.rewardPercentage.toFixed(2)}%`,
      `Expected Value,${result.expectedValue.toFixed(2)}`,
      `Profit Factor,${result.profitFactor.toFixed(2)}`,
      `Minimum Win Rate Required,${result.winRateRequired.toFixed(2)}%`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'risk-reward-calculation.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      result: { ...result }
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { 
        riskPerTrade: "1", 
        rrRatio: "3", 
        probability: "70" 
      },
      moderate: { 
        riskPerTrade: "2", 
        rrRatio: "2", 
        probability: "60" 
      },
      aggressive: { 
        riskPerTrade: "3", 
        rrRatio: "1.5", 
        probability: "50" 
      }
    };
    
    // Calculate target price based on desired R:R ratio
    const currentInputs = { ...inputs };
    const entry = parseFloat(currentInputs.entryPrice);
    const stop = parseFloat(currentInputs.stopLossPrice);
    const riskPerUnit = Math.abs(entry - stop);
    const desiredReward = riskPerUnit * presets[preset].rrRatio;
    const targetPrice = currentInputs.positionType === "long" 
      ? (entry + desiredReward).toFixed(2)
      : (entry - desiredReward).toFixed(2);

    setInputs(prev => ({ 
      ...prev, 
      ...presets[preset],
      targetPrice 
    }));
    addNotification(`${preset} trading preset applied`, "info");
  };

  // Fixed Chart Data with proper tooltip configurations
  const rrChartData = result ? {
    labels: ["Potential Reward", "Potential Risk", "Expected Value"],
    datasets: [{
      data: [
        Math.max(result.totalReward, 0),
        Math.max(result.totalRisk, 0),
        Math.max(result.expectedValue, 0)
      ],
      backgroundColor: ["#28A745", "#FF6B6B", "#4ECDC4"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const scenarioChartData = scenarioResults.length > 0 ? {
    labels: scenarioResults.map(s => s.name),
    datasets: [{
      label: 'Potential Outcome',
      data: scenarioResults.map(s => s.outcome),
      backgroundColor: scenarioResults.map(s => s.outcome >= 0 ? '#28A745' : '#FF6B6B'),
      borderWidth: 2
    }]
  } : null;

  const positionMetrics = calculatePositionSizing();

  return (
    <div className="profit-calculator">
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Risk/Reward Ratio Calculator</h1>
        </div>
        <p>Advanced risk analysis, position sizing, and trading scenario simulations</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.name}</div>
                    <div className="saved-details">
                      <span className={`profit ${calc.result.rrRatio >= 2 ? 'positive' : 'negative'}`}>
                        R:R {calc.result.rrRatio.toFixed(2)}
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'R:R Calculator'}
              </button>
              <button 
                className={`tab ${activeTab === "sizing" ? "active" : ""}`}
                onClick={() => setActiveTab("sizing")}
              >
                <i className="fas fa-ruler"></i>
                {isMobile ? 'Sizing' : 'Position Sizing'}
              </button>
              <button 
                className={`tab ${activeTab === "scenarios" ? "active" : ""}`}
                onClick={() => setActiveTab("scenarios")}
              >
                <i className="fas fa-chart-bar"></i>
                {isMobile ? 'Scenarios' : 'Scenario Analysis'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Trading Presets:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-chart-line"></i>
                    Trade Setup
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrows-up-down"></i> Position Type</label>
                      <select name="positionType" value={inputs.positionType} onChange={handleChange}>
                        <option value="long">Long</option>
                        <option value="short">Short</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Entry Price</label>
                      <input 
                        type="number" 
                        name="entryPrice" 
                        value={inputs.entryPrice} 
                        onChange={handleChange}
                        placeholder="100.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-bullseye"></i> Target Price</label>
                      <input 
                        type="number" 
                        name="targetPrice" 
                        value={inputs.targetPrice} 
                        onChange={handleChange}
                        placeholder="120.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-down"></i> Stop-Loss Price</label>
                      <input 
                        type="number" 
                        name="stopLossPrice" 
                        value={inputs.stopLossPrice} 
                        onChange={handleChange}
                        placeholder="95.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-layer-group"></i> Quantity</label>
                      <input 
                        type="number" 
                        name="quantity" 
                        value={inputs.quantity} 
                        onChange={handleChange}
                        placeholder="100"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-rocket"></i> Leverage</label>
                      <select name="leverage" value={inputs.leverage} onChange={handleChange}>
                        <option value="1">1x (No Leverage)</option>
                        <option value="2">2x</option>
                        <option value="3">3x</option>
                        <option value="5">5x</option>
                        <option value="10">10x</option>
                      </select>
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Cost & Probability
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-coins"></i> Brokerage Fees</label>
                      <input 
                        type="number" 
                        name="brokerage" 
                        value={inputs.brokerage} 
                        onChange={handleChange}
                        placeholder="5.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-file-invoice-dollar"></i> Taxes</label>
                      <input 
                        type="number" 
                        name="taxes" 
                        value={inputs.taxes} 
                        onChange={handleChange}
                        placeholder="2.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Win Probability (%)</label>
                      <input 
                        type="number" 
                        name="probability" 
                        value={inputs.probability} 
                        onChange={handleChange}
                        placeholder="60"
                        step="1"
                        min="1"
                        max="99"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Slippage</label>
                      <input 
                        type="number" 
                        name="slippage" 
                        value={inputs.slippage} 
                        onChange={handleChange}
                        placeholder="0.50"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-hand-holding-usd"></i> Financing Cost</label>
                      <input 
                        type="number" 
                        name="financingCost" 
                        value={inputs.financingCost} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrows-repeat"></i> Currency Rate</label>
                      <input 
                        type="number" 
                        name="currencyConversion" 
                        value={inputs.currencyConversion} 
                        onChange={handleChange}
                        placeholder="1.00"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Position Sizing Tab */}
          {activeTab === "sizing" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-ruler-combined"></i>
                  Risk Management
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-exclamation-triangle"></i> Risk Amount ($)</label>
                    <input 
                      type="number" 
                      name="riskAmount" 
                      value={inputs.riskAmount} 
                      onChange={handleChange}
                      placeholder="100"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-piggy-bank"></i> Portfolio Size ($)</label>
                    <input 
                      type="number" 
                      name="portfolioSize" 
                      value={inputs.portfolioSize} 
                      onChange={handleChange}
                      placeholder="10000"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-percentage"></i> Risk Per Trade (%)</label>
                    <input 
                      type="number" 
                      name="riskPerTrade" 
                      value={inputs.riskPerTrade} 
                      onChange={handleChange}
                      placeholder="2"
                      step="0.1"
                    />
                  </div>
                </div><br/>

                <h3>
                  <i className="fas fa-chart-line"></i>
                  Advanced Settings
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-wave-square"></i> Volatility (%)</label>
                    <input 
                      type="number" 
                      name="volatility" 
                      value={inputs.volatility} 
                      onChange={handleChange}
                      placeholder="15"
                      step="0.1"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-clock"></i> Timeframe (Days)</label>
                    <input 
                      type="number" 
                      name="timeframe" 
                      value={inputs.timeframe} 
                      onChange={handleChange}
                      placeholder="30"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-trailer"></i> Trailing Stop (%)</label>
                    <input 
                      type="number" 
                      name="trailingStop" 
                      value={inputs.trailingStop} 
                      onChange={handleChange}
                      placeholder="0"
                      step="0.1"
                    />
                  </div>
                </div><br/>

                {positionMetrics && (
                  <div className="risk-results">
                    <h4>Position Sizing Results</h4>
                    <div className="risk-metrics">
                      <div className="risk-metric">
                        <span className="metric-label">By Risk Amount:</span>
                        <span className="metric-value">{positionMetrics.positionSizeByRisk} shares</span>
                      </div>
                      <div className="risk-metric">
                        <span className="metric-label">By Portfolio:</span>
                        <span className="metric-value">{positionMetrics.positionSizeByPortfolio} shares</span>
                      </div>
                      <div className="risk-metric">
                        <span className="metric-label">Optimal Size:</span>
                        <span className="metric-value">{positionMetrics.optimalSize} shares</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Scenario Analysis Tab */}
          {activeTab === "scenarios" && (
            <div className="risk-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-chart-bar"></i>
                  Scenario Analysis
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-bullseye"></i> Multiple Targets</label>
                    <input 
                      type="number" 
                      name="multipleTargets" 
                      value={inputs.multipleTargets} 
                      onChange={handleChange}
                      placeholder="1"
                      min="1"
                      max="5"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-sign-out-alt"></i> Partial Exit (%)</label>
                    <input 
                      type="number" 
                      name="partialExit" 
                      value={inputs.partialExit} 
                      onChange={handleChange}
                      placeholder="0"
                      step="5"
                      min="0"
                      max="100"
                    />
                  </div>
                </div><br/>

                {scenarioResults.length > 0 && (
                  <div className="position-breakdown">
                    <h4>Scenario Outcomes</h4><br/>
                    <div className="scenario-list">
                      {scenarioResults.map((scenario, index) => (
                        <div key={index} className="scenario-item">
                          <div className="breakdown-item">
                            <span className="scenario-name">{scenario.name}</span>
                            <span className={`scenario-outcome ${scenario.outcome >= 0 ? 'positive' : 'negative'}`}>
                              ${scenario.outcome.toFixed(2)}
                            </span>
                          </div>
                          <div className="breakdown-item">
                            <span>Probability: {scenario.probability.toFixed(1)}%</span>
                            <span>{scenario.description}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className={`history-profit ${calc.result.rrRatio >= 2 ? 'positive' : 'negative'}`}>
                          <i className={`fas ${calc.result.rrRatio >= 2 ? 'fa-arrow-up' : 'fa-arrow-down'}`}></i>
                          R:R {calc.result.rrRatio.toFixed(2)}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-percentage"></i> Risk: {calc.result.riskPercentage.toFixed(2)}%</span>
                        <span><i className="fas fa-bullseye"></i> Reward: {calc.result.rewardPercentage.toFixed(2)}%</span>
                        <span><i className="fas fa-chart-line"></i> EV: ${calc.result.expectedValue.toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "sizing" || activeTab === "scenarios") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-balance-scale"></i>
                    Risk/Reward Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className={`result-value ${result.rrRatio >= 2 ? 'positive' : result.rrRatio >= 1 ? 'moderate' : 'negative'}`}>
                          {result.rrRatio.toFixed(2)}:1
                        </div>
                        <div className="result-label">
                          <i className="fas fa-balance-scale"></i>
                          Risk/Reward Ratio
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value positive">
                          ${result.totalReward.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-bullseye"></i>
                          Total Reward
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value negative">
                          ${result.totalRisk.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-exclamation-triangle"></i>
                          Total Risk
                        </div>
                      </div>
                      <div className="result-card">
                        <div className={`result-value ${result.expectedValue >= 0 ? 'positive' : 'negative'}`}>
                          ${result.expectedValue.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-chart-line"></i>
                          Expected Value
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-percentage"></i> Risk Percentage:</span>
                        <span>{result.riskPercentage.toFixed(2)}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-bullseye"></i> Reward Percentage:</span>
                        <span>{result.rewardPercentage.toFixed(2)}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-hand-holding-usd"></i> Margin Required:</span>
                        <span>${result.marginRequired.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-calculator"></i> Profit Factor:</span>
                        <span>{result.profitFactor.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-chart-line"></i> Annualized Return:</span>
                        <span>{result.annualizedReturn.toFixed(2)}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-shield-alt"></i> Min Win Rate Required:</span>
                        <span>{result.winRateRequired.toFixed(2)}%</span>
                      </div>
                    </div>

                    {/* Advanced Metrics */}
                    <div className="risk-summary">
                      <h4>Advanced Metrics</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className={`risk-value ${result.kellyCriterion > 0 ? 'positive' : 'negative'}`}>
                            {(result.kellyCriterion * 100).toFixed(1)}%
                          </div>
                          <div className="risk-label">Kelly Criterion</div>
                        </div>
                        <div className="risk-card">
                          <div className={`risk-value ${result.riskAdjustedReturn > 1 ? 'positive' : 'negative'}`}>
                            {result.riskAdjustedReturn.toFixed(2)}
                          </div>
                          <div className="risk-label">Risk-Adjusted Return</div>
                        </div>
                      </div>
                    </div>

                    {/* Chart - FIXED TOOLTIP */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Risk vs Reward Distribution
                      </h3>
                      <Doughnut 
                        data={rrChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  const value = context.parsed;
                                  if (typeof value === 'number' && !isNaN(value)) {
                                    return `$${value.toFixed(2)}`;
                                  }
                                  return `$${0}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-balance-scale fa-3x"></i>
                    <p>Enter your trade details to see risk/reward analysis</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "sizing" && positionMetrics && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-ruler-combined"></i>
                  Position Sizing Results
                </h3>
                
                <div className="projection-summary">
                  <div className="summary-card">
                    <div className="summary-value">
                      {positionMetrics.optimalSize}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-ruler"></i>
                      Optimal Shares
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      ${(positionMetrics.optimalSize * parseFloat(inputs.entryPrice)).toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-dollar-sign"></i>
                      Position Value
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {((positionMetrics.optimalSize * Math.abs(parseFloat(inputs.entryPrice) - parseFloat(inputs.stopLossPrice))) / parseFloat(inputs.portfolioSize) * 100).toFixed(2)}%
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-percentage"></i>
                      Portfolio Risk
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      ${(positionMetrics.optimalSize * Math.abs(parseFloat(inputs.entryPrice) - parseFloat(inputs.stopLossPrice))).toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-exclamation-triangle"></i>
                      Total Risk
                    </div>
                  </div>
                </div><br/>

                <div className="risk-results-panel">
                  <h3>
                    <i className="fas fa-analytics"></i>
                    Risk Assessment
                  </h3>
                  <div className="risk-visualization">
                    <div className="position-breakdown">
                      <h4>Position Breakdown</h4>
                      <div className="breakdown-item">
                        <span>Risk per Share:</span>
                        <span>${Math.abs(parseFloat(inputs.entryPrice) - parseFloat(inputs.stopLossPrice)).toFixed(2)}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Maximum Position by Risk:</span>
                        <span>{positionMetrics.positionSizeByRisk} shares</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Maximum Position by Portfolio:</span>
                        <span>{positionMetrics.positionSizeByPortfolio} shares</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Recommended Position:</span>
                        <span>{positionMetrics.optimalSize} shares</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "scenarios" && scenarioResults.length > 0 && (
              <div className="risk-results-panel">
                <h3>
                  <i className="fas fa-chart-bar"></i>
                  Scenario Analysis
                </h3><br/>
                <div className="chart-container">
                  <Bar 
                    data={scenarioChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: false
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              const value = context.parsed;
                              if (typeof value === 'number' && !isNaN(value)) {
                                return `$${value.toFixed(2)}`;
                              }
                              return `$${0}`;
                            }
                          }
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: function(value) {
                              return '$' + value.toLocaleString();
                            }
                          }
                        }
                      }
                    }}
                  />
                </div><br/>
                
                <div className="position-breakdown">
                  <h4>Scenario Summary</h4>
                  <div className="scenario-stats">
                    <div className="breakdown-item">
                      <span>Best Case:</span>
                      <span className="positive">${Math.max(...scenarioResults.map(s => s.outcome)).toFixed(2)}</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Worst Case:</span>
                      <span className="negative">${Math.min(...scenarioResults.map(s => s.outcome)).toFixed(2)}</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Expected Value:</span>
                      <span className="moderate">${(scenarioResults.reduce((sum, s) => sum + (s.outcome * s.probability / 100), 0)).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default RiskRewardCalculator;